import { CSSProperties } from 'react';
import classNames from 'classnames';

import { PostData } from './model/PostTypes';
import { TEMPLATE_ICONS } from './model/TemplateIcons';
import { TemplateId } from '@lifeserver/shared';

import { getPostMedia } from './portalUtils';
import styles from './PostThumbnail.module.scss';

export interface PostThumbnailProps {
    height?: number;
    postData: PostData;
    templateId?: TemplateId;
    className?: string;
    style?: CSSProperties;
    zoomOnHover?: boolean;
}

export const PostThumbnail = ({
    className,
    style,
    postData,
    templateId,
    height = 32,
    zoomOnHover = true,
}: PostThumbnailProps) => {
    const medias = getPostMedia(postData);

    // If no media available, show template icon as fallback
    if (
        (!medias || !Array.isArray(medias) || medias.length === 0) &&
        templateId
    ) {
        const templateIcon = TEMPLATE_ICONS[templateId];
        const aspectRatio = getAspectRatio(postData.size);
        const thumbnailWidth = Math.round(height * aspectRatio);

        return (
            <div
                className={classNames(styles.container, className)}
                style={{
                    height,
                    width: thumbnailWidth,
                    ...style,
                }}
            >
                <img
                    src={templateIcon}
                    alt={`${templateId} template`}
                    className={classNames(
                        styles.thumbnail,
                        styles.templateIcon,
                    )}
                    style={{
                        width: thumbnailWidth,
                        height,
                        objectFit: 'fill',
                    }}
                    draggable={false}
                />
            </div>
        );
    }

    if (!medias || !Array.isArray(medias) || medias.length === 0) {
        return null;
    }

    const aspectRatio = getAspectRatio(postData.size);
    const thumbnailWidth = Math.round(height * aspectRatio);
    return (
        <div
            className={classNames(styles.container, className, {
                [styles.zoomOnHover]: zoomOnHover,
            })}
            style={{
                height,
                width:
                    // 0.2 because css transforms all except first thumbnail
                    // 80% left + 8px margin per thumbnail
                    thumbnailWidth * (1 + (medias.length - 1) * 0.2) +
                    medias.length * 8,
                ...style,
            }}
        >
            {medias.map((media, i) =>
                !media ? (
                    <div
                        style={{
                            width: thumbnailWidth,
                            height,
                        }}
                        key={i}
                        className={styles.thumbnail}
                    />
                ) : media.mediaType === 'Image' ? (
                    <img
                        width={thumbnailWidth}
                        height={height}
                        key={i}
                        src={media.url.replace('{0}', '@1x')}
                        className={styles.thumbnail}
                        alt={`Image on page ${i + 1}`}
                        draggable={false}
                    />
                ) : (
                    <video
                        width={thumbnailWidth}
                        height={height}
                        key={i}
                        src={media.url + '#t=0.1'} // Move time to 0.1s to get a proper thumbnail, but still be at start
                        className={styles.thumbnail}
                        aria-label={`Video on page ${i + 1}`}
                        muted
                        loop
                        draggable={false}
                        onPointerEnter={(e) => {
                            e.currentTarget.play();
                        }}
                        onPointerLeave={(e) => {
                            e.currentTarget.pause();
                        }}
                    />
                ),
            )}
        </div>
    );
};

const getAspectRatio = (size: PostData['size']): number => {
    switch (size) {
        case 'Large':
            return 16 / 9;
        case 'Medium':
            return 1 / 1;
        case 'Small':
            return 420 / 512;
    }
};
